import { CUTTING_SHAPE_ID } from '@/domain/selection/reservedSelectionIds';
import { CUTTING_OUTLINE_NAME } from '@/infrastructure/engine2d/fabricGroupsName';
import { Group, type Canvas, type FabricObject } from 'fabric';
import type { IGroup } from '@/infrastructure/engine2d/types/IGroup';

function addElementToLockedBackgroundObjectGroup(fabricCanvas: Canvas, groupName: string, element: FabricObject) {
    const lockedGroup = (fabricCanvas.getObjects('group') as Group[]).find((group) => {
        return (group as Group & { id: string }).id === groupName;
    });
    if (!lockedGroup) {
        throw new Error('Locked background group not found, it need to be initialized with createLockedBackgroundObjectGroup function');
    }
    const isObjectinsideCanvas = fabricCanvas.getObjects().includes(element);
    if (isObjectinsideCanvas) {
        fabricCanvas.remove(element);
    }
    element.set({ selectable: false, evented: false });
    lockedGroup.add(element);
    lockedGroup.set('dirty', true);
    fabricCanvas.renderAll();
}

function createLockedBackgroundObjectGroup(fabricCanvas: Canvas, groupName: string) {
    const lockedGroup = new Group([], {
        selectable: false,
        evented: false,
        visible: true,
    });

    lockedGroup.set('id', groupName);
    fabricCanvas.add(lockedGroup);
    fabricCanvas.sendObjectToBack(lockedGroup);
    const bgGroup = fabricCanvas.getObjects('group').find((group) => {
        return !(group as Group & { id: string }).id;
    });
    if (bgGroup) {
        // If this is called before display canvas fullScreen, then canvas center on the top left corner, not at center
        fabricCanvas.sendObjectToBack(bgGroup);
    }
}

function createCuttingGroup(fabricCanvas: Canvas, groupName: string) {
    const cuttingGroup = new Group([], {
        visible: true,
        selectable: false,
        evented: false,
    });
    cuttingGroup.set('id', groupName);
    fabricCanvas.add(cuttingGroup);
    return cuttingGroup;
}

function keepLockedBackgroundObjectGroupToBack(fabricCanvas: Canvas, groupName: string) {
    const groups = fabricCanvas.getObjects('group') as IGroup[];
    const bgLockedGroup = groups.find((group) => group.id === groupName);
    if (bgLockedGroup) {
        fabricCanvas.sendObjectToBack(bgLockedGroup);
    }
    const bgGroup = groups.find((group) => !group.id);
    if (bgGroup) {
        fabricCanvas.sendObjectToBack(bgGroup);
    }
    const cuttingGroup = groups.find((group) => group.id === CUTTING_SHAPE_ID);
    if (cuttingGroup) {
        fabricCanvas.bringObjectToFront(cuttingGroup);
    }
    const cuttingOutline = groups.find((group) => group.id === CUTTING_OUTLINE_NAME);
    if (cuttingOutline) {
        fabricCanvas.bringObjectToFront(cuttingOutline);
    }
    fabricCanvas.renderAll();
}

// groupId undefined → match the group with no id (the background group).
function getFabricGroupById(fabricCanvas: Canvas, groupId?: string): IGroup | undefined {
    return (fabricCanvas.getObjects('group') as IGroup[]).find((group) => {
        if (groupId) {
            return group.id === groupId;
        }
        return group.id === undefined;
    });
}

export {
    addElementToLockedBackgroundObjectGroup,
    createCuttingGroup,
    createLockedBackgroundObjectGroup,
    getFabricGroupById,
    keepLockedBackgroundObjectGroupToBack,
};
